import { isPlainRecord } from "./plain-data.ts";

const decoder = new TextDecoder("utf-8", { fatal: true, ignoreBOM: false });

export function copyConcreteUint8Array(value: unknown): Uint8Array {
  try {
    if (!(value instanceof Uint8Array) || Object.getPrototypeOf(value) !== Uint8Array.prototype || !(value.buffer instanceof ArrayBuffer)) {
      throw new Error("not a concrete Uint8Array");
    }
    return new Uint8Array(value);
  } catch {
    throw new Error("invalid byte carrier");
  }
}

export function strictDecodeUtf8(bytes: Uint8Array): string {
  if (bytes.byteLength >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    throw new Error("UTF-8 BOM is forbidden");
  }
  const text = decoder.decode(bytes);
  if (text.charCodeAt(0) === 0xfeff || text.includes("\uFFFD")) throw new Error("invalid UTF-8 JSON");
  return text;
}

/** JSON recursive descent which rejects duplicate keys before materializing records. */
export function parseStrictJson(text: string): unknown {
  let cursor = 0;
  const whitespace = (): void => { while (cursor < text.length && /[\u0009\u000a\u000d\u0020]/.test(text[cursor]!)) cursor += 1; };
  const parseValue = (): unknown => {
    whitespace();
    const char = text[cursor];
    if (char === "{") return parseObject();
    if (char === "[") return parseArray();
    if (char === '"') return parseString();
    for (const [literal, value] of [["true", true], ["false", false], ["null", null]] as const) {
      if (text.startsWith(literal, cursor)) { cursor += literal.length; return value; }
    }
    const match = /-?(?:0|[1-9][0-9]*)(?:\.[0-9]+)?(?:[eE][+-]?[0-9]+)?/y;
    match.lastIndex = cursor;
    const found = match.exec(text);
    if (found === null) throw new Error("invalid JSON token");
    cursor = match.lastIndex;
    const number = Number(found[0]);
    if (!Number.isFinite(number)) throw new Error("invalid JSON number");
    return number;
  };
  const parseString = (): string => {
    const start = cursor;
    cursor += 1;
    for (;;) {
      const char = text[cursor];
      if (char === undefined || char === "\n" || char === "\r" || char.charCodeAt(0) < 0x20) throw new Error("invalid JSON string");
      if (char === '"') {
        cursor += 1;
        return JSON.parse(text.slice(start, cursor)) as string;
      }
      if (char === "\\") {
        cursor += 1;
        const escape = text[cursor];
        if (escape === "u") {
          if (!/^[0-9a-fA-F]{4}$/.test(text.slice(cursor + 1, cursor + 5))) throw new Error("invalid JSON escape");
          cursor += 5;
          continue;
        }
        if (escape === undefined || !'"\\/bfnrt'.includes(escape)) throw new Error("invalid JSON escape");
      }
      cursor += 1;
    }
  };
  const parseArray = (): unknown[] => {
    cursor += 1; whitespace(); const output: unknown[] = [];
    if (text[cursor] === "]") { cursor += 1; return output; }
    for (;;) {
      output.push(parseValue()); whitespace();
      if (text[cursor] === "]") { cursor += 1; return output; }
      if (text[cursor] !== ",") throw new Error("invalid JSON array");
      cursor += 1;
    }
  };
  const parseObject = (): Record<string, unknown> => {
    cursor += 1; whitespace(); const output: Record<string, unknown> = {}; const keys = new Set<string>();
    if (text[cursor] === "}") { cursor += 1; return output; }
    for (;;) {
      whitespace(); if (text[cursor] !== '"') throw new Error("invalid JSON object key");
      const key = parseString();
      if (keys.has(key)) throw new Error("duplicate JSON key");
      keys.add(key); whitespace(); if (text[cursor] !== ":") throw new Error("invalid JSON object"); cursor += 1;
      output[key] = parseValue(); whitespace();
      if (text[cursor] === "}") { cursor += 1; return output; }
      if (text[cursor] !== ",") throw new Error("invalid JSON object"); cursor += 1;
    }
  };
  const value = parseValue(); whitespace();
  if (cursor !== text.length || (typeof value === "object" && value !== null && !Array.isArray(value) && !isPlainRecord(value))) {
    throw new Error("trailing or invalid JSON");
  }
  return value;
}

export function parseStrictUtf8Json(bytes: Uint8Array): unknown {
  return parseStrictJson(strictDecodeUtf8(bytes));
}
